import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { RoleService } from '../services/role/role.service';
import { LicensingRole } from '../shared/constants/licensing-role.enum';

/**
 * Role Guard — Protects routes that require a minimum licensing role.
 *
 * Reads the `minimumRole` value from route data and checks the user's
 * resolved role via RoleService. Redirects unauthorized users to the
 * dashboard root.
 *
 * Usage in routes:
 * {
 *   path: 'licensing/plans',
 *   canActivate: [roleGuard],
 *   data: { minimumRole: LicensingRole.TenantAdmin }
 * }
 */
export const roleGuard: CanActivateFn = async (route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> => {
  const roleService: RoleService = inject(RoleService);
  const router: Router = inject(Router);

  // Ensure role is resolved from Okta (idempotent — no-op after first call)
  await roleService.initialize();

  const minimumRole: LicensingRole | undefined = route.data?.['minimumRole'] as LicensingRole | undefined;

  if (!minimumRole || roleService.hasRole(minimumRole)) {
    return true;
  }

  // User does not meet the minimum role — redirect to dashboard
  return router.parseUrl('/');
};
